import { type AuthToken, getAuthenticationService } from '@arcual/lib-app-platform';
import { defineStore } from 'pinia';
import { computed, type ComputedRef, readonly, type Ref, ref } from 'vue';
import { env } from '@/constants';
import jwtDecode from 'jwt-decode';
import { resetAllStores } from '../storeManager';

export const AuthenticationStoreName = 'AuthenticationStore';
export const useAuthenticationStore = defineStore(AuthenticationStoreName, () => {
  const authToken = ref<AuthToken | null>(null);
  const guestEmail = ref<string | null>(null);

  const setAuthToken = (token: AuthToken | null) => {
    authToken.value = token;
  };

  const setGuestEmailAuth = (email: string | null) => {
    guestEmail.value = email;
    getAuthenticationService().setGuestEmailAuth(email);
  };

  const login = async (email: string, password: string) => {
    return getAuthenticationService().login(email, password);
  };

  const reset = () => {
    authToken.value = null;
    guestEmail.value = null;

    setAuthToken(null);
  };

  const logout = async () => {
    await getAuthenticationService().logout();
    resetAllStores();
  };

  const resetPassword = async (email: string) => await getAuthenticationService().resetPassword(email);

  const checkEmailExists = async (email: string) => await getAuthenticationService().checkEmailExists(email);

  const changeEmail = async (email: string, activationId?: string, redirectTo?: string) => {
    await getAuthenticationService().changeEmail(email, activationId, redirectTo);
  };

  const getAuthToken = (): Ref<AuthToken | null> => {
    return readonly(authToken);
  };

  const getGuestEmail = (): Ref<string | null> => {
    return readonly(guestEmail);
  };

  const isAuthenticated = (): ComputedRef<boolean> => computed(() => !!authToken.value);

  const isVerified = (): ComputedRef<boolean> => {
    return computed<boolean>(() => {
      if (authToken.value) {
        const token = jwtDecode(authToken.value.accessToken) as never;
        return !!token[`${env.VITE_AUTH0_CLAIMS_NAMESPACE}/claims/verified`];
      }

      return false;
    });
  };

  const userEmail = (): ComputedRef<string | null> => {
    return computed<string | null>(() => {
      if (authToken.value) {
        const token = jwtDecode(authToken.value.accessToken) as never;
        return token[`${env.VITE_AUTH0_CLAIMS_NAMESPACE}/claims/email`] || null;
      }

      return null;
    });
  };

  return {
    login,
    logout,
    resetPassword,
    getAuthToken,
    setAuthToken,
    isAuthenticated,
    isVerified,
    userEmail,
    getGuestEmail,
    setGuestEmailAuth,
    reset,
    checkEmailExists,
    changeEmail,
  };
});
